/* eslint-disable react/jsx-props-no-spreading */
import get from 'lodash-es/get';
import React, { useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { useConfig } from '../../hooks/useConfig';
import { useConstants } from '../../hooks/useConstants';
import { getRequestJSON, uploadToS3Request } from '../../util/services';
import Upload, { UploadProps } from './Upload';

function getFileExtension(file) {
  const splitName = file.name.split('.');
  return splitName[splitName.length - 1];
}

interface S3UploadProps extends Omit<UploadProps, 'onChange' | 'accept'> {
  accept?: string;
  s3InfoUrl: string;
  generateKey: Function;
  preserveName?: boolean;
  onStart?: Function;
  onSuccess?: Function;
  onFailure?: Function;
}

function S3Upload(props: S3UploadProps) {
  const { apiHost } = useConfig();
  const { constants } = useConstants();
  const { name, accept = '', s3InfoUrl, generateKey, preserveName, multiple, onStart, onSuccess, onFailure } = props;

  const S3_DIRECT_UPLOAD_URL = get(constants, 'S3_DIRECT_UPLOAD_URL');
  const [loading, setLoading] = useState(false);

  const form = useFormContext();
  const { clearErrors, control } = form;
  const { field } = useController({ control, name });

  const onDropAccepted = async (acceptedFiles) => {
    setLoading(true);
    if (clearErrors) {
      clearErrors();
    }
    if (onStart) {
      onStart(acceptedFiles);
    }

    const url = `${apiHost}${s3InfoUrl}`;
    const response = await getRequestJSON(url);
    const { bucket, form_data: payload } = response;

    const linkArray = [];
    let linkString = '';
    let path;
    const generatedKey = generateKey();

    for (let i = 0; i < acceptedFiles.length; i += 1) {
      const file = acceptedFiles[i];
      const fileExtension = getFileExtension(file);
      let s3Key;
      if (multiple || preserveName) {
        path = `${generatedKey}/`;
        s3Key = `${path}${file.name}`;
        if (preserveName) {
          path = s3Key;
        }
        linkString = `${S3_DIRECT_UPLOAD_URL}/${bucket}/${s3Key}`;
        linkArray.push(linkString);
      } else {
        path = `${generatedKey}.${fileExtension}`;
        s3Key = path;
        linkString = `${S3_DIRECT_UPLOAD_URL}/${bucket}/${s3Key}`;
      }

      const uploadToS3RequestAsync = (linkStringParam, pathParm, index) => {
        uploadToS3Request(S3_DIRECT_UPLOAD_URL, `/${bucket}`, payload, file, s3Key)
          .then(() => {
            if (index === acceptedFiles.length - 1) {
              const links = multiple ? linkArray : linkStringParam;

              field.onChange(pathParm, {
                shouldDirty: true,
                shouldValidate: true,
              });

              if (onSuccess) {
                onSuccess(links, pathParm, acceptedFiles);
              }
            }
          })
          .catch((error) => {
            if (onFailure) {
              onFailure(error);
            }
          })
          .finally(() => {
            setLoading(false);
          });
      };

      uploadToS3RequestAsync(linkString, path, i);
    }
  };

  return <Upload {...props} name={name} accept={accept} onChange={onDropAccepted} loading={loading} />;
}

export default S3Upload;
